import acorn from "acorn"
import estraverse from "./embedded_extlibs/estraverse-5.3.0.js"
import escodegen from "./embedded_extlibs/escodegen-2.1.0.js"
import execute from "./execute.js"

let globalId = 0

function CodeExplorer (source, {
    name = "source",
    sourcePath,
    prependWith = "",
    syntaxErrorHandler,
    executionErrorHandler,
    errorMessages,
    evaluateInAsyncFunction
} = {}) {

    this.id           = globalId++  // unique id to prevent issues when concatenating
                                    // several tainted codes from different codeExplorers

    this.prependWith  = prependWith
    this.source       = source
    this.name         = name
    this.sourcePath   = sourcePath  // editor path of the source when it differs from name
                                    // (page runs use an execution id as name)
    this.evalSelfName = `codeExplorer${this.id}`

    this.syntaxErrorHandler    = syntaxErrorHandler
    this.executionErrorHandler = executionErrorHandler

    this.evaluateInAsyncFunction = evaluateInAsyncFunction

    parse(this, {errorMessages})

}



function parse (explorer, {errorMessages = {}}) {
    let {source, name} = explorer

    try {
        explorer.parsedCode = acorn.parse(source, {
            ecmaVersion: 2021,
            locations: true,
            localizedMessages: errorMessages,
            allowAwaitOutsideFunction: explorer.evaluateInAsyncFunction,

            // align parser with execution: user code runs via new Function() with "use strict",
            // so catch strict-mode reserved words (public, private, ...) here with nice messages
            // instead of letting the native JS engine throw a generic "Unexpected strict mode reserved word"
            impliedStrict: true
        })
    } catch (error) {

        error.toxiTrace = error.toxiTrace || []
        error.toxiTrace.unshift("Toxilibs - code_explorer.js - parse - try catch acorn.parse")

        error.sourceCode      = source
        error.sourceName      = name
        explorer.parsingError = true

        if (explorer.syntaxErrorHandler) {
            explorer.syntaxErrorHandler(error, {
                line:       error.lineNumber,
                column:     error.column,
                sourceName: name
            })
        } else {
            throw error
        }
    }
}






//************************************


CodeExplorer.prototype.forNodes = function ({type, types, unscoped, iterator}) {

    let nodeTypes = types || [type]

    function callIterator (node, parent) {
        if (nodeTypes.includes(node.type)) {
            iterator(node, parent)
        }
    }

    let params = {
        leave: callIterator
    }

    if (unscoped) {
        params.enter = skipScopes
    }

    estraverse.traverse(this.parsedCode, params)
}


CodeExplorer.prototype.findNodes = function ({type, types, unscoped}) {

    let result = []
    function fillResult (node) {
        result.push(node)
    }

    this.forNodes({
        type,
        types,
        unscoped,
        iterator: fillResult
    })

    return result
}


CodeExplorer.prototype.listIdentifiers = function () {
    let identifiers = []

    function pushNodeName ({name}) {
        if (identifiers.indexOf(name) === -1) {
            identifiers.push(name)
        }
    }

    this.forNodes({type: "Identifier",  iterator: pushNodeName})

    return identifiers.sort()
}



CodeExplorer.prototype.listVariables = function () {
    let identifiers = []

    function filter (node, parent) {
        if (!["Property", "MemberExpression"].includes(parent.type)) {
            let {name} = node
            if (!identifiers.includes(name)) {
                identifiers.push(name)
            }
        }
    }

    this.forNodes({type: "Identifier", iterator: filter})

    return identifiers.sort()
}



CodeExplorer.prototype.listDeclaredVariables = function () {
    return this
        .findNodes({type: "VariableDeclarator"})
        .map(idNameAndLoc)
}






CodeExplorer.prototype.listGlobalsAdded = function () {
    return this
        .findNodes({
            types:    ["VariableDeclarator", "FunctionDeclaration"],
            unscoped: true
        })
        .map(function ({id, loc, type}) {
            return {
                name: id.name,
                type: (type === "VariableDeclarator" ? "variable" : "function"),
                loc
            }
        })
}



CodeExplorer.prototype.listUnscopedFunctionCalls = function () {
    return this
        .findNodes({
            type:    "CallExpression",
            unscoped: true
        })
        .map(function ({callee}) {
            let {loc} = callee
            if (callee.type === "Identifier") {
                return {
                    name: callee.name,
                    loc
                }
            } else if (callee.type === "MemberExpression") {
                return {
                    name: callee.property.name,
                    loc
                }
            } else if (callee.type === "FunctionExpression") {
                return {
                    name: callee.id,
                    loc
                }
            }
            return callee
        })

}



function skipScopes (node) {
    if (node.type === "FunctionExpression" || node.type === "FunctionDeclaration") {
        this.skip()
    }
}


function idNameAndLoc ({id, loc}) {
    let {name} = id
    return {name, loc}
}








//************************************


CodeExplorer.prototype.callbackTainter = function ({pos, name, node, fnName, alone}) {
    let {line, column} = pos

    let args = [{
        type:  "Literal",
        value: line,
        raw:   String(line)
    }, {
        type:  "Literal",
        value: column,
        raw:   String(column)
    }]

    if (typeof name !== "undefined") {
        args.push({
            type:  "Literal",
            value: name,
            raw:   `"${name}"`
        })
    }

    if (node) {
        args.push(node)
    }


    let newNode = {
        type: "CallExpression",
        callee: {
            type: "Identifier",
            name: fnName + this.id
        },
        arguments: args
    }

    if (alone) {
        return {
            type: "ExpressionStatement",
            expression: newNode
        }
    } else {
        return newNode
    }

}


CodeExplorer.prototype.addVariableMeta = function (node) {
    let {loc, init, id} = node
    node.init = this.callbackTainter({
        fnName: "assignmentCallback",
        pos:    loc.start,
        name:   id.name,
        node:   init
    })
}


// for stuffs like i++
CodeExplorer.prototype.addUpdateMeta = function (node) {
    node.prefix = true
    return this.callbackTainter({
        fnName: "assignmentCallback",
        pos:    node.loc.start,
        name:   node.argument.name,
        node
    })
}


CodeExplorer.prototype.addAssignmentMeta = function (node) {
    let {operator, loc, left, right} = node
    if (operator === "=") {
        node.right = this.callbackTainter({
            fnName: "assignmentCallback",
            pos:    loc.start,
            name:   left.name,
            node:   right
        })
        return false
    } else {
        return {
            loc,
            type: "AssignmentExpression",
            left,
            operator: "=",
            right: this.callbackTainter({
                fnName: "assignmentCallback",
                pos:    loc.start,
                name:   left.name,
                node
            })
        }
    }
}


CodeExplorer.prototype.addConditionMeta = function (node) {
    node.test = this.callbackTainter({
        fnName: "conditionCallback",
        pos:    node.loc.start,
        name:   escodegen.generate(node.test),
        node:   node.test
    })
}


// "var __lgCounter = 0", injected at the top of every function body (and the Program) that the
// loop guard touches. It MUST be a plain local of the enclosing function: a counter captured from
// an outer scope and mutated in a hot loop deoptimizes V8 (measured ~4x slower); a local is free.
// "var" (not "let") so hoisting keeps it valid however the loops nest, and re-initializes per call.
// loc borrows the scope's first statement so addPositions (which reads subNode.loc) stays happy.
function loopCounterDeclaration (loc) {
    return {
        type: "VariableDeclaration",
        kind: "var",
        loc,
        declarations: [{
            type: "VariableDeclarator",
            id:   {type: "Identifier", name: "__lgCounter"},
            init: {type: "Literal", value: 0, raw: "0"}
        }]
    }
}


// Injected as an ExpressionStatement at the top of each loop body:
//   (++__lgCounter & 1023) === 0 && __lgCheck(locIndex)
// near-zero cost per iteration (one increment + one bitmask test); __lgCheck only runs every 1024.
CodeExplorer.prototype.loopGuardStatement = function (node) {
    let locIndex = this.loopGuard.registerLoopLocation({
        line:       node.loc.start.line,
        column:     node.loc.start.column,
        sourceName: this.name
    })

    // keep the loop's loc so escodegen maps the injected line back to the loop's original
    // position: the thrown error's stack frame then resolves to the exact loop line
    return {
        type: "ExpressionStatement",
        loc:  node.loc,
        expression: {
            type: "LogicalExpression",
            operator: "&&",
            left: {
                type: "BinaryExpression",
                operator: "===",
                left: {
                    type: "BinaryExpression",
                    operator: "&",
                    left: {
                        type: "UpdateExpression",
                        operator: "++",
                        prefix: true,
                        argument: {type: "Identifier", name: "__lgCounter"}
                    },
                    right: {type: "Literal", value: 1023, raw: "1023"}
                },
                right: {type: "Literal", value: 0, raw: "0"}
            },
            right: {
                type: "CallExpression",
                callee: {type: "Identifier", name: "__lgCheck"},
                arguments: [{type: "Literal", value: locIndex, raw: String(locIndex)}]
            }
        }
    }
}


// wraps a coordinate-carrying object literal in __tagObject(literal, entryIndex) so the
// runtime object remembers where its numbers live in the source (see coordinate_source_refs.js)
CodeExplorer.prototype.tagObjectLiteral = function (node) {
    let entryIndex = this.objectTagger.registerFromNode(node, {
        sourceName: this.sourcePath || this.name,
        source:     this.source
    })

    if (entryIndex === null) {
        return null
    }

    return {
        type: "CallExpression",
        loc:  node.loc,
        callee: {type: "Identifier", name: this.objectTagger.helperName},
        arguments: [
            node,
            {type: "Literal", value: entryIndex, raw: String(entryIndex)}
        ]
    }
}


const functionTypes = ["FunctionDeclaration", "FunctionExpression", "ArrowFunctionExpression"]


// the statement array of a scope node (Program, or a block-bodied function), else null.
// that array is where the loop counter local must be declared.
function scopeBodyOf (node) {
    if (node.type === "Program") {
        return node.body
    }
    if (functionTypes.includes(node.type) && node.body.type === "BlockStatement") {
        return node.body.body
    }
    return null
}


// nearest enclosing scope body, walking ancestors from innermost outward
function enclosingScopeBody (parents) {
    for (let i = parents.length - 1; i >= 0; i--) {
        let body = scopeBodyOf(parents[i])
        if (body) {
            return body
        }
    }
    return null
}


function ensureBlockBody (node) {
    if (node.body.type !== "BlockStatement") {
        node.body = {
            type: "BlockStatement",
            body: [node.body],
            loc:  node.body.loc
        }
    }
}


CodeExplorer.prototype.handleLoop = function (node) {
    if (this.loopGuard) {
        ensureBlockBody(node)
        node.body.body.unshift(this.loopGuardStatement(node))
    }

    if (this.callbacks.loopInside) {
        ensureBlockBody(node)
        node.body.body.unshift(this.callbackTainter({
            alone:  true,
            fnName: "loopInsideCallback",
            pos:    node.loc.start
        }))
    }

    let newBody = [node]

    if (this.callbacks.loopBefore) {
        newBody.unshift(this.callbackTainter({
            alone:  true,
            fnName: "loopBeforeCallback",
            pos:    node.loc.start
        }))
    }

    if (this.callbacks.loopAfter) {
        newBody.push(this.callbackTainter({
            alone:  true,
            fnName: "loopAfterCallback",
            pos:    node.loc.start
        }))
    }

    if (newBody.length === 1) {
        return undefined
    }

    return {
        type: "BlockStatement",
        body: newBody,
        loc:  node.loc
    }

}


CodeExplorer.prototype.addAwaitWatcher = function (node) {
    return {
        type: "AwaitExpression",
        loc:  node.loc,
        argument: this.callbackTainter({
            fnName: "awaitCallback",
            pos:    node.loc.start,
            node:   node.argument
        })
    }
}



// To find the correct syntax we used this trick :
// let lineToAdd = 'positionCallback' + this.id + '(' + start.line + ', ' + start.column + ')'
// let taint = esprima.parseScript(lineToAdd).body[0]
// console.log(JSON.stringify(taint, null, 4))

function positionCallbackNode (id, {line, column}) {
    return {
        type: "ExpressionStatement",
        expression: {
            type: "CallExpression",
            callee: {
                type: "Identifier",
                name: `positionCallback${id}`
            },
            arguments: [
                {
                    type: "Literal",
                    value: line
                },
                {
                    type: "Literal",
                    value: column
                }
            ]
        }
    }
}


CodeExplorer.prototype.addPositions = function (node) {
    let newBody = []

    node.body.forEach(subNode => {
        let {start} = subNode.loc
        let taint   = positionCallbackNode(this.id, start)
        newBody.push(taint)
        newBody.push(subNode)
    })

    node.body = newBody
}


CodeExplorer.prototype.handleAssignment = function (node) {
    let {type} = node
    if (type === "VariableDeclarator") {
        return this.addVariableMeta(node)
    } else if (type === "UpdateExpression") {
        return this.addUpdateMeta(node)
    } else if (type === "AssignmentExpression") {
        return this.addAssignmentMeta(node)
    }
    return false
}














/*eslint-disable complexity */
CodeExplorer.prototype.createCallbacks = function (handler) {

    let {name: sourceName} = this

    this.callbacks = {}

    if (handler.assignmentCallback) {
        this.callbacks.assignment = function (line, column, label, value) {
            handler.assignmentCallback({sourceName, line, column, label, value})
            return value
        }
    }

    if (handler.conditionCallback) {
        this.callbacks.condition = function (line, column, label, value) {
            handler.conditionCallback({sourceName, line, column, label, value: Boolean(value)})
            return value
        }
    }

    if (handler.loopInsideCallback) {
        this.callbacks.loopInside = function (line, column) {
            handler.loopInsideCallback({sourceName, line, column})
        }
    }

    if (handler.loopBeforeCallback) {
        this.callbacks.loopBefore = function (line, column) {
            handler.loopBeforeCallback({sourceName, line, column})
        }
    }

    if (handler.loopAfterCallback) {
        this.callbacks.loopAfter = function (line, column) {
            handler.loopAfterCallback({sourceName, line, column})
        }
    }

    if (handler.positionCallback) {
        this.callbacks.position = function (line, column) {
            handler.positionCallback({sourceName, line, column})
        }
    } else if (handler.followPosition) {
        this.callbacks.position = (line, column) => {
            this.lastExecutionPoint = {sourceName, line, column}
        }
    }

    if (handler.awaitCallback) {
        this.callbacks.await = async function (line, column, promise) {
            let params = {sourceName, line, column}
            handler.awaitCallback(params)
            let value
            if (params.errorHandler) {
                try {
                    value = await promise
                } catch (error) {
                    params.errorHandler(error)
                    return null
                }
            } else {
                value = await promise
            }
            if (params.callback) {
                params.callback(value)
            }
            return value
        }
    }
}





//Tip see here : https://github.com/estree/estree/blob/master/spec.md

const loopTypes = [
    "ForStatement",
    "WhileStatement",
    "DoWhileStatement",
    "ForOfStatement",
    "ForInStatement"
]


CodeExplorer.prototype.taintCode = function (handler = {}) {

    if (this.parsingError) {
        throw Error("Code Explorer can not taint code because parsing has not been completed.")
    }

    this.loopGuard    = handler.loopGuard
    this.objectTagger = handler.objectTagger

    this.createCallbacks(handler)

    let explorer    = this
    let {callbacks} = this

    if (callbacks.await && this.findNodes({type: "AwaitExpression"}).length === 0) {
        delete callbacks.await
    }

    // no taint needed: keep the source exactly as written (real code in devtools,
    // identity line mapping), only prepended with preSource (callback vars + prependWith)
    if (Object.keys(callbacks).length === 0 && !this.loopGuard && !this.objectTagger) {
        let preSource = this.preSource()
        this.preSourceLineCount = preSource.split("\n").length - 1
        this.taintedCode = preSource + this.source
        return
    }

    // bodies (arrays) of scopes that directly contain an instrumented loop and therefore need
    // a local "var __lgCounter = 0" injected at their top (see loopCounterDeclaration)
    let scopesNeedingCounter = new Set()

    let newTree = estraverse.replace(this.parsedCode, {
        leave: function (node) { //eslint-disable-line complexity

            let {type} = node
            let r

            if (callbacks.assignment) {
                r = explorer.handleAssignment(node)
                if (r) {
                    return r
                }
            }

            if (callbacks.condition && type === "IfStatement") {
                r = explorer.addConditionMeta(node)
                if (r) {
                    return r
                }
            }

            if (loopTypes.includes(type)) {
                if (explorer.loopGuard) {
                    let scopeBody = enclosingScopeBody(this.parents())
                    if (scopeBody) {
                        scopesNeedingCounter.add(scopeBody)
                    }
                }
                r = explorer.handleLoop(node)
                if (r) {
                    return r
                }
            }

            if (explorer.loopGuard) {
                let scopeBody = scopeBodyOf(node)
                if (scopeBody && scopesNeedingCounter.has(scopeBody)) {
                    let firstLoc = scopeBody.length > 0 ? scopeBody[0].loc : node.loc
                    scopeBody.unshift(loopCounterDeclaration(firstLoc))
                }
            }

            if (callbacks.position && (type === "Program" || type === "BlockStatement")) {
                r = explorer.addPositions(node)
                if (r) {
                    return r
                }
            }

            if (callbacks.await && type === "AwaitExpression") {
                r = explorer.addAwaitWatcher(node)
                if (r) {
                    return r
                }
            }

            if (explorer.objectTagger && type === "ObjectExpression") {
                r = explorer.tagObjectLiteral(node)
                if (r) {
                    return r
                }
            }

            return undefined
        }
    })

    let {code, map} = escodegen.generate(newTree, {sourceMapWithCode: true, sourceMap: "sourceMap"})
    let preSource      = this.preSource()
    let preSourceLines = preSource.split("\n").length - 1

    this.sourceMappings = map._mappings._array

    this.sourceMappings.forEach(m => {
        m.generatedLine += preSourceLines
    })
    this.taintedCode = preSource + code

}


CodeExplorer.prototype.findOriginalPosition = function ({line, column = 1}) {
    if (!this.sourceMappings) {
        return {line: line - (this.preSourceLineCount || 0), column, sourceName: this.name}
    }


    let result = this.sourceMappings.findLast(({generatedLine, generatedColumn}) => {
        return (generatedLine < line) || (
            generatedLine === line &&
            generatedColumn <= column - 1
        )
    }) || {}


    return {
        line:       result.originalLine,
        column:     result.originalColumn + (column - result.generatedColumn),
        sourceName: this.name
    }
}



/*eslint-enable complexity */


CodeExplorer.prototype.preSource = function () {
    let s = ""

    for (let name in this.callbacks) {
        s += `var ${name}Callback${this.id} = ${this.evalSelfName}.callbacks.${name}\n`
    }

    s += this.prependWith

    return s
}



CodeExplorer.prototype.normalizedCode = function () {
    return escodegen.generate(this.parsedCode)
}


// where the engine reports a compilation error without any position, acorn has one.
// Passed to execute() so it can locate an error thrown by new Function() in the source
// it generates (scope preamble + wrapper function around the tainted code).
CodeExplorer.locateSyntaxError = function (source) {
    try {
        acorn.parse(source, {
            ecmaVersion: 2021,
            locations:   true,
            impliedStrict: true,
            allowReturnOutsideFunction: true, // the wrapper is called from a top level return
            allowAwaitOutsideFunction:  true
        })
    } catch (error) {
        return {line: error.lineNumber, column: error.column, message: error.message}
    }
    return null // acorn accepts what the engine refused : no position to offer
}


CodeExplorer.sourcesAreEquivalent = function (sourceA, sourceB) {
    let normalizedA = escodegen.generate(acorn.parse(sourceA, {
        ecmaVersion: 2021,
        allowAwaitOutsideFunction: true
    }))
    let normalizedB = escodegen.generate(acorn.parse(sourceB, {
        ecmaVersion: 2021,
        allowAwaitOutsideFunction: true
    }))
    return normalizedA === normalizedB
}



CodeExplorer.prototype.evaluate = async function ({
    scope = {},
    thisValue,
    receiveInContextEvaluator,
    receiveGetCurrentLine,
    inContextEvaluatorErrorHandler,
    autoReturn
} = {}) {

    if (this.parsingError) {
        throw new Error("Code Explorer can not evaluate code because parsing has not been completed.")
    }

    Object.defineProperty(scope, this.evalSelfName, {
        enumerable: false,
        get: () => this
    })


    const result = await execute({
        name:   this.name,
        source: this.taintedCode || this.source,
        scope,
        thisValue,
        errorHandler: this.executionErrorHandler,
        findOriginalPosition: params => this.findOriginalPosition(params),
        syntaxErrorLocator:   CodeExplorer.locateSyntaxError,
        receiveInContextEvaluator,
        receiveGetCurrentLine,
        inContextEvaluatorErrorHandler, //FIXME
        autoReturn,
        useAsyncFunction: this.evaluateInAsyncFunction
    })

    return result
}





export default CodeExplorer

